/**
 * Productlane support widget loader (TELV1-483).
 *
 * Installs Productlane's official bootstrap stub (which queues API calls until
 * the widget script arrives), initializes it with our widget key, and injects
 * the CDN script. Only the authenticated dashboard app loads the widget — the
 * respondent-facing chat/embed apps and super-admin never do.
 */

const WIDGET_SRC = 'https://widget.productlane.com/latest.productlane-widget.min.js';

type ProductlaneMethod = (...args: unknown[]) => void;

interface ProductlaneApi {
  queue?: Record<string, { args: IArguments }>;
  init: ProductlaneMethod;
  set: ProductlaneMethod;
  open: ProductlaneMethod;
  close: ProductlaneMethod;
  toggle: ProductlaneMethod;
  on: ProductlaneMethod;
  off: ProductlaneMethod;
  enable: ProductlaneMethod;
  disable: ProductlaneMethod;
  signOut?: ProductlaneMethod;
}

declare global {
  interface Window {
    Productlane?: ProductlaneApi;
  }
}

export function initProductlane(widgetKey: string, userToken?: string) {
  if (window.Productlane) return;

  // Productlane's install snippet (restructured for lint): stub methods queue
  // their calls until the real widget script replaces them.
  const stub = { queue: {} } as unknown as ProductlaneApi;
  (['set', 'open', 'close', 'toggle', 'on', 'off', 'init', 'enable', 'disable'] as const).forEach(
    (m) => {
      stub[m] = function queueCall(...args: unknown[]) {
        stub.queue![m] = { args: args as unknown as IArguments };
      };
    },
  );
  window.Productlane = stub;

  // The token must ride along with init: a `set({ userToken })` queued through
  // the stub before the CDN script loads does not survive initialization
  // (verified against the live widget), while init-time tokens always apply.
  stub.init(userToken ? { widgetKey, userToken } : { widgetKey });

  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.crossOrigin = 'anonymous';
  script.src = WIDGET_SRC;
  document.head.appendChild(script);
}

// Clears the widget session on logout so the next user isn't attributed to
// the previous one. Safe to call whether or not the widget ever loaded.
export function signOutProductlane() {
  const productlane = window.Productlane;
  if (productlane && typeof productlane.signOut === 'function') {
    productlane.signOut();
  }
}
